import React, { useCallback, useEffect, useLayoutEffect, useMemo } from "react";

import {
  ConnectionHandler,
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
} from "react-relay";
import { FlatList } from "react-native";
import {
  AHIcon,
  AHSeparator,
  AHTodoCategoryIcon,
  Box,
  ListItem,
  useBoxStyle,
} from "arkh";
import { TodoEditMoveToCategorySelector_todoCategories$key } from "./__generated__/TodoEditMoveToCategorySelector_todoCategories.graphql";
import { useNavigationHeaderOptions } from "../../common/navigationHeaderOptions";
import { TodoEditMoveToCategoryMutation } from "./__generated__/TodoEditMoveToCategoryMutation.graphql";
import { useIntl } from "react-intl";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorParamList,
  UserLoggedInNavigatorScreenProps,
} from "../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import {
  NativeStackNavigationProp,
  createNativeStackNavigator,
} from "@react-navigation/native-stack";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { TodoEditMoveToCategoryQuery } from "./__generated__/TodoEditMoveToCategoryQuery.graphql";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";

const Stack = createNativeStackNavigator<ParamList>();
type ParamList = {
  TodoMoveToCategoryMain: UserLoggedInNavigatorParamList["TodoMoveToCategory"];
};

function TodoEditMoveToCategoryScreen(props: {
  navigation: CompositeNavigationProp<
    NativeStackNavigationProp<ParamList, "TodoMoveToCategoryMain">,
    UserLoggedInNavigatorNavigationProp<"TodoMoveToCategory">
  >;
  route: RouteProp<ParamList, "TodoMoveToCategoryMain">;
}) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fk) => <TodoEditMoveToCategoryScreenC {...props} fetchKey={fk} />}
    </LazyQueryNetworkBoundrary>
  );
}

function TodoEditMoveToCategoryScreenC(props: {
  navigation: CompositeNavigationProp<
    NativeStackNavigationProp<ParamList, "TodoMoveToCategoryMain">,
    UserLoggedInNavigatorNavigationProp<"TodoMoveToCategory">
  >;
  route: RouteProp<ParamList, "TodoMoveToCategoryMain">;
  fetchKey: string;
}) {
  const id = props.route.params.todoId;
  const setOptions = props.navigation.setOptions;
  const data = useLazyLoadQuery<TodoEditMoveToCategoryQuery>(
    graphql`
      query TodoEditMoveToCategoryQuery($id: ID!) {
        currentStore @required(action: THROW) {
          id
          todoCategories(includeDefault: true) {
            id
            ...TodoEditMoveToCategorySelector_todoCategories
          }
        }
        todo(id: $id) {
          id
          categoryId
          teams {
            id
          }
        }
      }
    `,
    {
      id,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [selectedCategoryId, setSelectedCategoryId] = React.useState(
    data.todo?.categoryId
  );

  useEffect(() => {
    setSelectedCategoryId(data.todo?.categoryId);
  }, [data.todo?.categoryId]);

  const disabled = selectedCategoryId === data.todo?.categoryId;

  const teamIds = useMemo(
    () => data.todo?.teams.map((team) => team.id) ?? [],
    [data.todo?.teams]
  );

  const { currentEmployeeId } = usePersistedEmployeeId();
  const [moveMutation, isInFlightMutation] =
    useMutation<TodoEditMoveToCategoryMutation>(graphql`
      mutation TodoEditMoveToCategoryMutation(
        $todoId: ID!
        $newCategoryId: ID!
        $employeeId: ID
      ) {
        todo(todoId: $todoId, employeeId: $employeeId) {
          moveToCategory(newCategoryId: $newCategoryId) {
            todo {
              categoryId
              tags {
                color
                id
                title
              }
              category {
                tags {
                  id
                  color
                  title
                }
              }
            }
          }
        }
      }
    `);

  const onSave = useCallback(() => {
    if (!selectedCategoryId || !data.todo) return;
    moveMutation({
      variables: {
        newCategoryId: selectedCategoryId,
        todoId: data.todo.id,
        employeeId: currentEmployeeId,
      },
      updater: (store) => {
        if (data.todo?.categoryId) {
          const category = store.get(data.todo.categoryId);

          teamIds.forEach((teamId) => {
            const categoryTeam = category?.getLinkedRecord("team", {
              id: teamId,
            });

            if (categoryTeam) {
              const connection1 = ConnectionHandler.getConnection(
                categoryTeam,
                "TodoCategoryScreen_todos",
                { assignedToMe: false }
              );

              const connection2 = ConnectionHandler.getConnection(
                categoryTeam,
                "TodoCategoryScreen_todos",
                { assignedToMe: true }
              );

              if (connection1 && data.todo?.id) {
                ConnectionHandler.deleteNode(connection1, data.todo.id);
              }
              if (connection2 && data.todo?.id) {
                ConnectionHandler.deleteNode(connection2, data.todo.id);
              }
            }
          });
        }
      },
      onCompleted: () => {
        props.navigation.goBack();
      },
    });
  }, [
    data.todo,
    moveMutation,
    props.navigation,
    selectedCategoryId,
    teamIds,
    currentEmployeeId,
  ]);

  const onCancel = useCallback(() => {
    props.navigation.goBack();
  }, [props.navigation]);

  const getOptions = useTodoMoveToCategoryMainOptions();
  useLayoutEffect(() => {
    setOptions(
      getOptions({
        onCancel,
        onSave,
        saveLoading: isInFlightMutation,
        disabled,
      })
    );
  });

  if (!data.todo || !selectedCategoryId) return null;

  return (
    <TodoEditCategorySelector
      todoCategories={data.currentStore.todoCategories}
      selectedCategoryId={selectedCategoryId}
      setSelectedCategoryId={setSelectedCategoryId}
    />
  );
}

function TodoEditCategorySelector(props: {
  todoCategories: TodoEditMoveToCategorySelector_todoCategories$key;
  selectedCategoryId: string;
  setSelectedCategoryId: (categoryId: string) => void;
}) {
  const todoCategories = useFragment(
    graphql`
      fragment TodoEditMoveToCategorySelector_todoCategories on TodoCategory
      @relay(plural: true) {
        id
        title
        icon
        isDefault
      }
    `,
    props.todoCategories
  );

  const style = useBoxStyle({
    color: "background",
    space: "medium",
  });
  return (
    <FlatList
      contentInsetAdjustmentBehavior="automatic"
      data={todoCategories}
      style={[style, { flex: 1 }]}
      ItemSeparatorComponent={AHSeparator}
      keyboardShouldPersistTaps="handled"
      renderItem={({ item, index }) => {
        return (
          <ListItem
            border="border-primary"
            isFirst={index === 0}
            isLast={index + 1 === todoCategories.length}
            key={item.id}
            onPress={() => {
              props.setSelectedCategoryId(item.id);
            }}
            left={<AHTodoCategoryIcon icon={item.icon ?? "Check"} />}
            right={
              <ListItem.SelectIndicator
                selected={item.id === props.selectedCategoryId}
              />
            }
          >
            <Box style={{ flexDirection: "row", alignItems: "center" }}>
              <ListItem.Title>{item.title}</ListItem.Title>
              {item.isDefault ? (
                <AHIcon
                  spaceLeft="small"
                  color="text-washed"
                  type="crown"
                  size="small"
                />
              ) : null}
            </Box>
          </ListItem>
        );
      }}
    />
  );
}

function useTodoMoveToCategoryMainOptions() {
  const getOptions = useNavigationHeaderOptions();
  const intl = useIntl();
  const title = intl.formatMessage({
    defaultMessage: "Move to list",
    description: "TodoEditMoveToCategory:title",
  });
  return useCallback(
    (props: {
      onSave?: () => void;
      onCancel: () => void;
      saveLoading?: boolean;
      disabled?: boolean;
    }) =>
      getOptions({
        type: "SAVE",
        onCancel: props.onCancel,
        title,
        defaultContentStyle: true,
        disabled: props.disabled,
        saveLoading: !!props.saveLoading,
        onSave: props.onSave,
      }),
    [getOptions, title]
  );
}

function TodoEditMoveToCategoryNavigator(
  props: UserLoggedInNavigatorScreenProps<"TodoMoveToCategory">
) {
  const getOptions = useTodoMoveToCategoryMainOptions();
  const options = getOptions({
    onCancel: () => {
      props.navigation.goBack();
    },
  });
  return (
    <Stack.Navigator initialRouteName="TodoMoveToCategoryMain">
      <Stack.Screen
        name="TodoMoveToCategoryMain"
        component={TodoEditMoveToCategoryScreen}
        initialParams={props.route.params}
        options={options}
      />
    </Stack.Navigator>
  );
}

export const TodoEditMoveToCategory = withWebModal(
  TodoEditMoveToCategoryNavigator,
  true
);
